/**
 * Camera - Handles viewport and following entities
 */

import { Vector2 } from '../math/Vector2';
import { Vector3 } from '../math/Vector3';
import { TILE_WIDTH, TILE_HEIGHT } from './Renderer';

export class Camera {
  private position: Vector3 = new Vector3(0, 0, 0);
  private target: { position: Vector3 } | null = null;
  private smoothing: number = 5; // Higher = faster follow
  private bounds: { min: Vector3; max: Vector3 } | null = null;

  constructor() {}

  /** Set entity to follow */
  follow(entity: { position: Vector3 }): void {
    this.target = entity;
  }

  /** Stop following */
  stopFollowing(): void {
    this.target = null;
  }

  /** Set camera position directly */
  setPosition(pos: Vector3): void {
    this.position = pos.clone();
    this.clampToBounds();
  }

  /** Get current camera position */
  getPosition(): Vector3 {
    return this.position.clone();
  }

  /** Get offset for rendering (in screen coordinates) */
  getOffset(): Vector2 {
    // Convert camera world position to screen offset
    const screenX = (this.position.x - this.position.y) * (TILE_WIDTH / 2);
    const screenY = (this.position.x + this.position.y) * (TILE_HEIGHT / 2) - this.position.z;
    return new Vector2(screenX, screenY);
  }

  /** Set smoothing factor (higher = faster) */
  setSmoothing(value: number): void {
    this.smoothing = Math.max(0.1, value);
  }

  /** Set world bounds for camera */
  setBounds(min: Vector3, max: Vector3): void {
    this.bounds = { min, max };
  }

  /** Clear bounds */
  clearBounds(): void {
    this.bounds = null;
  }

  /** Update camera position */
  update(deltaTime: number): void {
    if (this.target) {
      // Smoothly interpolate towards target
      const targetPos = this.target.position;
      const dx = targetPos.x - this.position.x;
      const dy = targetPos.y - this.position.y;
      const dz = targetPos.z - this.position.z;

      const factor = 1 - Math.exp(-this.smoothing * deltaTime);

      this.position.x += dx * factor;
      this.position.y += dy * factor;
      this.position.z += dz * factor;

      this.clampToBounds();
    }
  }

  /** Move camera by offset */
  move(offset: Vector3): void {
    this.position = this.position.add(offset);
    this.clampToBounds();
  }

  private clampToBounds(): void {
    if (!this.bounds) return;

    this.position.x = Math.max(this.bounds.min.x, Math.min(this.bounds.max.x, this.position.x));
    this.position.y = Math.max(this.bounds.min.y, Math.min(this.bounds.max.y, this.position.y));
    this.position.z = Math.max(this.bounds.min.z, Math.min(this.bounds.max.z, this.position.z));
  }

  /** Shake camera (for damage effects, etc.) */
  private shakeIntensity: number = 0;
  private shakeDecay: number = 5;
  private shakeOffset: Vector2 = new Vector2(0, 0);

  shake(intensity: number): void {
    this.shakeIntensity = Math.max(this.shakeIntensity, intensity);
  }

  updateShake(deltaTime: number): Vector2 {
    if (this.shakeIntensity > 0.01) {
      this.shakeOffset = new Vector2(
        (Math.random() - 0.5) * this.shakeIntensity * 2,
        (Math.random() - 0.5) * this.shakeIntensity * 2
      );
      this.shakeIntensity *= Math.exp(-this.shakeDecay * deltaTime);
    } else {
      this.shakeIntensity = 0;
      this.shakeOffset = new Vector2(0, 0);
    }
    return this.shakeOffset;
  }
}
